import { Api } from '../api';
import * as WorkDoneCategory from './WorkDoneCategory';

/**
 * A WorkDone is logged against exactly one of these.
 */
export type Kind = 'company' | 'project' | 'serviceOrder' | 'ticket' | 'task';

/**
 * What a timer runs against, in the one shape every timer component reads.
 * Built either from the work itself, on a screen which lists it, or back out
 * of a WorkDone the API returned.
 */
export type Target = {
  kind: Kind;
  /**
   * Identifies the target, and is what a fresh timer starts against.  Always
   * built from the id rather than taken from `@id`, which is not canonical:
   * a resource read through a custom operation carries that operation's own
   * URI, so two views of the same work would otherwise disagree.
   */
  iri: string;
  title: string;
  /** Short prefix shown ahead of the rest of the meta, e.g. `#607`. */
  reference: string | null;
  /**
   * The client the work is for.  Null only where an entry no longer resolves
   * to anything nameable, e.g. one of the old task-target entries.
   */
  company: Api.Company.BaseView | null;
  /** Where the work opens in the app, when it has a page of its own. */
  path: string | null;
};

export function fromCompany(company: Api.Company.BaseView): Target {
  return {
    kind: 'company',
    iri: `/companies/${company.id}`,
    title: company.name,
    reference: null,
    company,
    path: `/company/${company.slug}`,
  };
}

export function fromProject(
  project: { id: string; name: string },
  company: Api.Company.BaseView
): Target {
  return {
    kind: 'project',
    iri: `/projects/${project.id}`,
    title: project.name,
    reference: null,
    company,
    path: null,
  };
}

export function fromServiceOrder(
  serviceOrder: { id: string; name: string },
  company: Api.Company.BaseView
): Target {
  return {
    kind: 'serviceOrder',
    iri: `/service-orders/${serviceOrder.id}`,
    title: serviceOrder.name,
    reference: null,
    company,
    path: `/projects/service-orders/${serviceOrder.id}`,
  };
}

export function fromTicket(
  ticket: { id: string; number: number; title: string },
  company: Api.Company.BaseView
): Target {
  return {
    kind: 'ticket',
    iri: `/tickets/${ticket.id}`,
    title: ticket.title,
    reference: `#${ticket.number}`,
    company,
    path: `/company/${company.slug}/tickets/${ticket.number}`,
  };
}

/**
 * Any view of a WorkDone which carries its target along with that target's
 * client, so the target can be rebuilt from it.
 */
type Targeted = Api.WorkDone.V_Timer | Api.WorkDone.V_List;

export function fromWorkDone(workDone: Targeted): Target | null {
  const { ticket, serviceOrder, project, company } = workDone;

  if (ticket) return fromTicket(ticket, ticket.company);
  if (serviceOrder) return fromServiceOrder(serviceOrder, serviceOrder.company);
  if (project) return fromProject(project, project.company);
  if (company) {
    return {
      ...fromCompany(company),
      // Client time has no name of its own, so what it was for stands in.
      reference: workDone.categoryString
        ? WorkDoneCategory.format(workDone.categoryString)
        : null,
    };
  }

  return null;
}

/**
 * Everything a timer can be pointed at, flattened out of the Projects screen's
 * summary for a picker: each client, then the projects and service orders
 * under them.  Tickets are left out — there are far too many to list, and the
 * ones worth logging against by hand are the ones recently worked on.
 */
export function fromProjectSummaries(
  summaries: ReadonlyArray<Api.CompanyProjectSummary.V>
): Array<Target> {
  return summaries.flatMap(({ company, projects, serviceOrders }) => [
    fromCompany(company),
    ...projects.flatMap(({ project, serviceOrders: theirs }) => [
      fromProject(project, company),
      ...theirs.map((serviceOrder) => fromServiceOrder(serviceOrder, company)),
    ]),
    ...serviceOrders.map((serviceOrder) =>
      fromServiceOrder(serviceOrder, company)
    ),
  ]);
}

/**
 * The categories which may be logged against this target, empty where the API
 * takes none.
 */
export function getCategories(target: Target): ReadonlyArray<string> {
  if (target.kind === 'company') return WorkDoneCategory.ALL;
  if (target.kind === 'project') return WorkDoneCategory.FOR_PROJECT;
  return [];
}

/**
 * Neither a client nor a project names what its time went on by itself, so
 * both hold the timer shut until a category is picked.
 */
export function isCategoryRequired(target: Target): boolean {
  return target.kind === 'company' || target.kind === 'project';
}

/**
 * The target part of the body for logging or correcting an entry.  The targets
 * it is not against are named too: a correction which only set the new one
 * would leave the entry pointing at two things, which the API refuses.
 */
export function getWriteBody(target: Target): Record<Kind, string | null> {
  return {
    company: null,
    project: null,
    serviceOrder: null,
    ticket: null,
    task: null,
    [target.kind]: target.iri,
  };
}

/**
 * The body for `POST /work-dones/start`, which takes exactly one target.
 */
export function getStartBody(
  target: Target,
  category: string | null
): Partial<Api.WorkDone.W_Start> {
  return {
    [target.kind]: target.iri,
    ...(category ? { categoryString: category } : {}),
  };
}
